<template>
  <div class="fixed inset-0 z-[120] flex items-center justify-center bg-ink/50 px-4">
    <div ref="cropPanel" role="dialog" aria-modal="true" tabindex="-1" class="w-full max-w-sm flex flex-col bg-sheet rounded-md shadow-ink overflow-hidden" @keydown="onCropKeydown">

      <div class="flex items-center gap-2 p-3.5 border-b border-[hsl(var(--border)/0.6)] shrink-0">
        <p class="flex-1 text-[15px] font-semibold">{{ $t('editProfile.cropTitle') }}</p>
        <button
          @click="$emit('cancel')"
          class="shrink-0 w-8 h-8 rounded-full flex items-center justify-center hover:bg-[hsl(var(--muted))] transition-colors"
          :aria-label="$t('liveHome.close')"
        >
          <X class="w-4 h-4" />
        </button>
      </div>

      <div class="p-4 flex flex-col items-center gap-3">
        <div
          ref="containerEl"
          class="relative w-full max-w-[300px] aspect-square mx-auto overflow-hidden rounded-md bg-[hsl(var(--muted))] touch-none select-none"
        >
          <img
            v-if="imageUrl"
            ref="imgEl"
            :src="imageUrl"
            alt=""
            class="absolute pointer-events-none"
            :style="imageStyle"
            @load="onImageLoad"
          />
          <div
            v-if="ready"
            class="absolute border-2 border-white cursor-move"
            style="box-shadow: 0 0 0 9999px rgba(0,0,0,0.55)"
            :style="boxStyle"
            @pointerdown="startMove"
          >
            <div
              v-for="handle in handles"
              :key="handle.corner"
              class="absolute w-6 h-6 -m-3 flex items-center justify-center touch-none"
              :style="{ left: handle.left, top: handle.top, cursor: handle.cursor }"
              @pointerdown.stop="startResize(handle.corner, $event)"
            >
              <div class="w-3.5 h-3.5 rounded-full bg-card shadow"></div>
            </div>
          </div>
        </div>
        <p class="text-[11px] text-ink2">{{ $t('editProfile.cropHint') }}</p>
      </div>

      <div class="flex items-center gap-2 px-4 py-3 border-t border-[hsl(var(--border)/0.6)] shrink-0">
        <button
          @click="$emit('cancel')"
          class="h-10 px-3 rounded-md bg-[hsl(var(--muted))] text-[12.5px] font-semibold hover:bg-[hsl(var(--muted)/0.8)] transition-colors"
        >
          {{ $t('routeRegistration.form.cancel') }}
        </button>
        <button
          @click="apply"
          :disabled="!ready"
          class="flex-1 h-10 rounded-md bg-[hsl(var(--primary))] text-white text-[13px] font-semibold hover:bg-[hsl(158,38%,30%)] transition-colors disabled:opacity-50"
        >
          {{ $t('editProfile.cropApply') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useDialogA11y } from "../composables/useDialogA11y";
import { computed, onBeforeUnmount, onMounted, reactive, ref } from "vue";
import { X } from "lucide-vue-next";

const props = defineProps<{
  file: File;
}>();

const emit = defineEmits<{
  (e: "cancel"): void;
  (e: "cropped", blob: Blob): void;
}>();

// Итоговое разрешение аватара - нигде в приложении он не отображается крупнее
// ~80px, поэтому 320px хватает даже для 3x-retina при небольшом размере файла.
const OUTPUT_SIZE = 320;
const MIN_BOX_SIZE = 60;

const imageUrl = URL.createObjectURL(props.file);
const containerEl = ref<HTMLDivElement | null>(null);
const imgEl = ref<HTMLImageElement | null>(null);
const ready = ref(false);

// Все величины ниже - в CSS px в локальных координатах контейнера (квадрат,
// левый верхний угол 0,0). Хранятся в reactive(), а не в обычных переменных,
// т.к. imageStyle/clampBox должны реагировать на изменения из onImageLoad().
let viewportSize = 300;
const layout = reactive({
  dispW: 0,
  dispH: 0,
  offsetX: 0,
  offsetY: 0,
  naturalWidth: 0,
  naturalHeight: 0,
});

const box = reactive({ x: 0, y: 0, size: 0 });

const imageStyle = computed(() => ({
  width: `${layout.dispW}px`,
  height: `${layout.dispH}px`,
  left: `${layout.offsetX}px`,
  top: `${layout.offsetY}px`,
}));

const boxStyle = computed(() => ({
  left: `${box.x}px`,
  top: `${box.y}px`,
  width: `${box.size}px`,
  height: `${box.size}px`,
}));

const handles = [
  { corner: "nw", left: "0px", top: "0px", cursor: "nwse-resize" },
  { corner: "ne", left: "100%", top: "0px", cursor: "nesw-resize" },
  { corner: "sw", left: "0px", top: "100%", cursor: "nesw-resize" },
  { corner: "se", left: "100%", top: "100%", cursor: "nwse-resize" },
] as const;

const clampBox = () => {
  box.size = Math.min(box.size, Math.min(layout.dispW, layout.dispH));
  box.size = Math.max(box.size, MIN_BOX_SIZE);
  box.x = Math.min(Math.max(box.x, layout.offsetX), layout.offsetX + layout.dispW - box.size);
  box.y = Math.min(Math.max(box.y, layout.offsetY), layout.offsetY + layout.dispH - box.size);
};

const onImageLoad = () => {
  if (!imgEl.value) return;
  layout.naturalWidth = imgEl.value.naturalWidth;
  layout.naturalHeight = imgEl.value.naturalHeight;
  const scale = Math.min(viewportSize / layout.naturalWidth, viewportSize / layout.naturalHeight);
  layout.dispW = layout.naturalWidth * scale;
  layout.dispH = layout.naturalHeight * scale;
  layout.offsetX = (viewportSize - layout.dispW) / 2;
  layout.offsetY = (viewportSize - layout.dispH) / 2;

  const initialSize = Math.min(layout.dispW, layout.dispH) * 0.9;
  box.size = initialSize;
  box.x = layout.offsetX + (layout.dispW - initialSize) / 2;
  box.y = layout.offsetY + (layout.dispH - initialSize) / 2;
  ready.value = true;
};

onMounted(() => {
  if (containerEl.value) viewportSize = containerEl.value.clientWidth;
});

// Pointer Events покрывают мышь, тач и перо одним кодом; setPointerCapture
// удерживает события move/up на элементе, даже если палец/курсор уходит с
// него при перетаскивании. Capture изредка бросает исключение в пограничных
// случаях - глотаем его, т.к. move/up и без capture работают в обычном случае.
const safeSetPointerCapture = (el: HTMLElement, pointerId: number) => {
  try {
    el.setPointerCapture(pointerId);
  } catch {
    // игнорируем
  }
};

const startMove = (evt: PointerEvent) => {
  const target = evt.currentTarget as HTMLElement;
  safeSetPointerCapture(target, evt.pointerId);
  const startX = evt.clientX;
  const startY = evt.clientY;
  const originX = box.x;
  const originY = box.y;

  const onMove = (e: PointerEvent) => {
    box.x = originX + (e.clientX - startX);
    box.y = originY + (e.clientY - startY);
    clampBox();
  };
  const onUp = (e: PointerEvent) => {
    target.removeEventListener("pointermove", onMove);
    target.removeEventListener("pointerup", onUp);
  };
  target.addEventListener("pointermove", onMove);
  target.addEventListener("pointerup", onUp);
};

const startResize = (corner: "nw" | "ne" | "sw" | "se", evt: PointerEvent) => {
  const target = evt.currentTarget as HTMLElement;
  safeSetPointerCapture(target, evt.pointerId);

  // Противоположный угол остаётся неподвижным якорем; позиция перетаскиваемого
  // угла определяет новый (всегда квадратный) размер. anchorX/Y и maxSize - в
  // локальных координатах контейнера; e.clientX/clientY (относительно страницы)
  // используются только как разница со стартовой позицией, не напрямую.
  const growsRight = corner === "ne" || corner === "se";
  const growsDown = corner === "sw" || corner === "se";
  const anchorX = growsRight ? box.x : box.x + box.size;
  const anchorY = growsDown ? box.y : box.y + box.size;
  const cornerStartX = growsRight ? box.x + box.size : box.x;
  const cornerStartY = growsDown ? box.y + box.size : box.y;
  const maxX = growsRight ? layout.offsetX + layout.dispW - anchorX : anchorX - layout.offsetX;
  const maxY = growsDown ? layout.offsetY + layout.dispH - anchorY : anchorY - layout.offsetY;
  const maxSize = Math.min(maxX, maxY);
  const startClientX = evt.clientX;
  const startClientY = evt.clientY;

  const onMove = (e: PointerEvent) => {
    const cornerX = cornerStartX + (e.clientX - startClientX);
    const cornerY = cornerStartY + (e.clientY - startClientY);
    const dx = growsRight ? cornerX - anchorX : anchorX - cornerX;
    const dy = growsDown ? cornerY - anchorY : anchorY - cornerY;
    const size = Math.min(Math.max(Math.max(dx, dy), MIN_BOX_SIZE), maxSize);
    box.size = size;
    box.x = growsRight ? anchorX : anchorX - size;
    box.y = growsDown ? anchorY : anchorY - size;
  };
  const onUp = (e: PointerEvent) => {
    target.removeEventListener("pointermove", onMove);
    target.removeEventListener("pointerup", onUp);
  };
  target.addEventListener("pointermove", onMove);
  target.addEventListener("pointerup", onUp);
};

const apply = () => {
  if (!ready.value || !imgEl.value) return;
  const scaleBack = layout.naturalWidth / layout.dispW;
  const sx = (box.x - layout.offsetX) * scaleBack;
  const sy = (box.y - layout.offsetY) * scaleBack;
  const sSize = box.size * scaleBack;

  const canvas = document.createElement("canvas");
  canvas.width = OUTPUT_SIZE;
  canvas.height = OUTPUT_SIZE;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.drawImage(imgEl.value, sx, sy, sSize, sSize, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
  canvas.toBlob(
    (blob) => {
      if (blob) emit("cropped", blob);
    },
    "image/jpeg",
    0.85,
  );
};

onBeforeUnmount(() => {
  URL.revokeObjectURL(imageUrl);
});

// Своя ловушка фокуса: окно кадрирования лежит поверх формы профиля, и Esc
// должен закрыть только его, не теряя несохранённый профиль.
const cropPanel = ref<HTMLElement | null>(null);
const { onKeydown: onCropKeydown } = useDialogA11y(cropPanel, { onClose: () => emit("cancel") });
</script>
